import { Component, effect, inject, signal, viewChild } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideX } from '@ng-icons/lucide';
import { BrnDialogContent } from '@spartan-ng/brain/dialog';
import { HlmDialogImports, HlmButtonImports } from '@pagai-fe/ui';
import { LoggedUserService } from '../../services/logged-user.service';
import { TutorialService } from '../../services/tutorial.service';
import { HlmDialog } from '@pagai-fe/ui';
import { RiveComponent } from '../../../shared/components/rive/rive.component';

/**
 * Hero welcome dialog shown on first access to the app
 * (gated by `User.hasAccessed === false`).
 *
 * Layout: split-pane — copy + actions on the left, Rive illustration
 * on the right with the close button.
 *
 * Any close path (X, "Esplora", outside the dialog) marks the user
 * as accessed via PATCH; "Esplora" additionally starts the welcome
 * tour.
 */
@Component({
  selector: 'app-welcome-dialog',
  standalone: true,
  imports: [NgIcon, HlmDialogImports, BrnDialogContent, HlmButtonImports, RiveComponent],
  providers: [provideIcons({ lucideX })],
  templateUrl: './welcome-dialog.component.html',
})
export class WelcomeDialogComponent {
  private readonly _loggedUserService = inject(LoggedUserService);
  private readonly _tutorialService = inject(TutorialService);

  private readonly _dialogRef = viewChild.required<HlmDialog>('dialog');

  /** Local guard so we open the dialog at most once per session. */
  private _opened = signal(false);

  constructor() {
    effect(() => {
      if (this._tutorialService.welcomeRequested() && !this._opened()) {
        this._opened.set(true);
        this._tutorialService.acknowledgeWelcome();
        // Defer one tick to let the viewChild resolve.
        setTimeout(() => this._dialogRef().open(), 0);
      }
    });
  }

  protected close(): void {
    this._dialogRef().close();
  }

  protected onClosed(): void {
    // Fired on any close path. Persist hasAccessed once and reset the
    // open guard so the dialog can be re-opened (e.g. from the sidebar logo).
    this._loggedUserService.markAccessed().subscribe();
    this._opened.set(false);
  }

  protected onExplore(): void {
    this.close();
    // Defer so the dialog backdrop unmounts before driver.js renders.
    setTimeout(() => this._tutorialService.startWelcomeTour(), 250);
  }
}
